import { css } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Grid, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { PinIcon } from '@coursera/cds-icons';

import ThreadBadge from 'bundles/discussions/components/forumsV2-ForumThreadCard/__components/ThreadBadge';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/index';

import { answerSorts } from '../../../constants';
import { ForumDropDownWithTheme } from '../../forumsV2-ForumDropDown';
import SectionDivider from '../../forumsV2-SectionDivider';
import { ShimmerSentence } from '../../forumsV2-ShimmerLib';
import type { ForumRepliesProps } from '../ForumReplies/__types__';
import AnswerBadge from '../__components/AnswerBadge';
import type { BadgeTypes } from '../__components/AnswerBadge/__types__';
import ForumItemCard, { ReplyCardShimmer } from '../__components/ForumItemCard';
import SoftDelete from '../__components/SoftDelete';
import type { ForumQuestionsDataProviderResponse } from '../__providers__/ForumPostDataProvider/__types__';

const { newestSort, popularSort } = answerSorts;

export function ShimmerState() {
  return (
    <div className="ForumThread">
      <h2 className="ForumThread__title">
        <ShimmerSentence width="125px" />
      </h2>
      <div className="ForumThread__content" style={{ overflowY: 'scroll', position: 'relative' }}>
        <ReplyCardShimmer />
        <SectionDivider />
        <ReplyCardShimmer />
      </div>
    </div>
  );
}

export default function ForumThread({
  isPinned,
  forumPost,
  children,
}: ForumQuestionsDataProviderResponse & {
  isPinned?: boolean;
  children?: ({ highlightedId, id, topLevelAnswerCount }: ForumRepliesProps) => JSX.Element;
}) {
  const isClosed = !!forumPost?.state?.closed;

  if (forumPost && forumPost.content && forumPost.content.details) {
    const topLevelAnswerCount = forumPost.topLevelAnswerCount;

    return (
      <section aria-label={_t('Forum Thread')} className="ForumThread">
        <Grid
          container
          xs={12}
          css={css`
            align-items: center;
          `}
        >
          <Grid
            item
            css={css`
              ${breakpoints.down('xs')} {
                flex: 1;
              }
            `}
          >
            <Typography variant="h2semibold" className="ForumThread___title">
              {isPinned && (
                <PinIcon
                  aria-label={_t('pinned forum post')}
                  size="medium"
                  css={{
                    margin: '0 var(--cds-spacing-100) 0 0',
                    background: 'var(--cds-color-yellow-200)',
                    padding: '3px',
                    height: '24px',
                    width: '24px',
                    borderRadius: '100%',
                    position: 'relative',
                    top: '6px',
                  }}
                />
              )}
              {forumPost.content.question}
            </Typography>
          </Grid>
          {isClosed && (
            <Grid
              item
              className="rc-ForumsV2_ForumThreadCard__content__tags"
              css={css`
                ${breakpoints.up('sm')} {
                  margin-left: 12px;
                  padding-top: 2px;
                }

                min-width: 100px;
              `}
            >
              <ThreadBadge badge="CLOSED" />
            </Grid>
          )}
        </Grid>
        <div className="ForumThread___content">
          <ForumItemCard
            cmlContent={forumPost.content.details}
            tags={
              <>
                {forumPost?.answerBadge?.answerBadge === 'HIGHLIGHTED' && (
                  <AnswerBadge answerBadge={forumPost.answerBadge.answerBadge} />
                )}
                {forumPost?.creator?.courseRole && (
                  <AnswerBadge answerBadge={forumPost.creator?.courseRole as BadgeTypes} />
                )}
              </>
            }
            creator={forumPost.creator}
            isUpvoted={forumPost.isUpvoted}
            upvoteCount={forumPost.upvoteCount}
            replyCount={topLevelAnswerCount}
            createdAt={forumPost.createdAt}
            courseId={forumPost.courseId}
            forumQuestionId={forumPost.questionId}
            deepLink={forumPost.deepLink}
            isClosed={isClosed}
            isRootCard
          />
          <SectionDivider />
          <div aria-label={_t('Replies')} className="rc-ForumItemReplyList">
            <Typography2 component="h3" variant="subtitleMedium" className="rc-ForumsV2__ForumItemReplyList__title">
              <FormattedMessage
                message={_t('{count, plural, =1 {1 Reply} =0 {0 Replies} other {{count} Replies}}')}
                count={topLevelAnswerCount}
              />
            </Typography2>
            {!!topLevelAnswerCount && topLevelAnswerCount > 1 && (
              <span className="rc-ForumsV2__ForumItemReplyList__sort_select">
                <ForumDropDownWithTheme<string, 'sort'>
                  options={[
                    { value: newestSort, param: 'sort', label: _t('Recent') },
                    { value: popularSort, param: 'sort', label: _t('Top') },
                  ]}
                  ariaLabelPrefix={_t('Sort By: ')}
                />
              </span>
            )}

            {children &&
              typeof children === 'function' &&
              children({
                id: forumPost?.id,
                highlightedId: forumPost?.forumAnswerBadgeTagMap?.HIGHLIGHTED?.forumAnswerId,
                topLevelAnswerCount: forumPost?.topLevelAnswerCount || 0,
              })}
          </div>
        </div>
        {forumPost?.state?.deleted && <SoftDelete entry={forumPost} showUndoDelete={true} isForQuestion={true} />}
      </section>
    );
  } else {
    return (
      <span>
        <ReplyCardShimmer />
      </span>
    );
  }
}
